import React, { useState, useEffect, useCallback } from "react";
import {
  RefreshCw,
  Database,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  TrendingUp,
  Search,
  Activity,
  BarChart3,
  X,
} from "lucide-react";
import { ticketService } from "../services/ticketService";
import {
  ZohoSyncJob,
  TicketStatsResponse,
  TicketHealthStatus,
  ZohoTicketSyncRequest,
} from "../types/tickets";
import TicketSyncModal from "./TicketSyncModal";
import TicketSearchInterface from "./TicketSearchInterface";

const TicketManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [syncJobs, setSyncJobs] = useState<ZohoSyncJob[]>([]);
  const [stats, setStats] = useState<TicketStatsResponse | null>(null);
  const [healthStatus, setHealthStatus] = useState<TicketHealthStatus | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [jobsResponse, statsResponse, healthResponse] = await Promise.all([
        ticketService.getRecentSyncJobs(10),
        ticketService.getTicketStats(),
        ticketService.getHealthStatus(),
      ]);

      setSyncJobs(jobsResponse.jobs);
      setStats(statsResponse);
      setHealthStatus(healthResponse);
    } catch (error) {
      console.error("Error fetching ticket data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll running jobs
  useEffect(() => {
    if (!pollingJobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const jobStatus = await ticketService.getSyncJobStatus(pollingJobId);

        // Update the job in the list
        setSyncJobs((prev) =>
          prev.map((job) => (job.job_id === pollingJobId ? jobStatus : job))
        );

        // Stop polling if job is complete
        if (["completed", "failed", "cancelled"].includes(jobStatus.status)) {
          setPollingJobId(null);
          fetchData(); // Refresh all data
        }
      } catch (error) {
        console.error("Error polling job status:", error);
        setPollingJobId(null);
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [pollingJobId, fetchData]);

  const handleStartSync = async (request: ZohoTicketSyncRequest) => {
    try {
      const response = await ticketService.startSync(request);
      setPollingJobId(response.job_id);
      setShowSyncModal(false);
      fetchData(); // Refresh to show new job
    } catch (error) {
      console.error("Error starting sync:", error);
    }
  };

  const handleCancelSync = async (jobId: string) => {
    try {
      await ticketService.cancelSyncJob(jobId);
      // Refresh data to show updated status
      fetchData();
      // Stop polling if this was the active job
      if (pollingJobId === jobId) {
        setPollingJobId(null);
      }
    } catch (error) {
      console.error("Error cancelling sync:", error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "cancelled":
        return <X className="w-4 h-4 text-orange-500" />;
      case "running":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "pending":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor(
      (seconds % 3600) / 60
    )}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Ticket Management
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400">
            Manage Zoho Desk ticket synchronization and search
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowSyncModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
          >
            <Play className="w-4 h-4" />
            Start Sync
          </button>
        </div>
      </div>

      {/* Health Status */}
      {healthStatus && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-5 h-5 text-neutral-700 dark:text-neutral-300" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              System Health
            </h3>
            <div
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                healthStatus.status === "healthy"
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
              }`}
            >
              {healthStatus.status}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(healthStatus.components).map(
              ([component, status]) => {
                if (component === "timestamp") return null;
                return (
                  <div key={component} className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        status ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    <span className="text-sm text-neutral-600 dark:text-neutral-400 capitalize">
                      {component.replace(/_/g, " ")}
                    </span>
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="flex space-x-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg p-1">
        {[
          { id: "dashboard", label: "Dashboard", icon: BarChart3 },
          { id: "sync-jobs", label: "Sync Jobs", icon: RefreshCw },
          { id: "search", label: "Search Tickets", icon: Search },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === id
                ? "bg-white dark:bg-neutral-700 text-orange-600 dark:text-orange-400 shadow-sm"
                : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === "dashboard" && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Database className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                Total Tickets
              </h3>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {stats.total_tickets.toLocaleString()}
            </p>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                Processed
              </h3>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {stats.processed_tickets.toLocaleString()}
            </p>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                Pending
              </h3>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {stats.pending_tickets.toLocaleString()}
            </p>
          </div>

          <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                Vector DB Size
              </h3>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {stats.vector_database_size.toLocaleString()}
            </p>
          </div>

          {/* Categories Chart */}
          <div className="md:col-span-2 lg:col-span-4 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
              Top Categories
            </h3>
            <div className="space-y-3">
              {Object.entries(stats.categories)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 10)
                .map(([category, count]) => (
                  <div key={category} className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-3">
                      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 min-w-0 flex-1">
                        {category}
                      </span>
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">
                        {count}
                      </span>
                    </div>
                    <div className="w-24 bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                      <div
                        className="bg-orange-500 h-2 rounded-full"
                        style={{
                          width: `${
                            (count /
                              Math.max(...Object.values(stats.categories))) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Sync Jobs Tab */}
      {activeTab === "sync-jobs" && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700">
          <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              Recent Sync Jobs
            </h3>
          </div>
          <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {syncJobs.map((job) => (
              <div key={job.job_id} className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(job.status)}
                    <div>
                      <h4 className="font-medium text-neutral-900 dark:text-neutral-100">
                        {job.job_type.charAt(0).toUpperCase() +
                          job.job_type.slice(1)}{" "}
                        Sync
                      </h4>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {formatDate(job.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {job.status === "running" &&
                      job.progress_percentage !== undefined
                        ? `${Math.round(job.progress_percentage)}%`
                        : job.status}
                    </div>
                    {job.duration_seconds && (
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">
                        {formatDuration(job.duration_seconds)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                {job.status === "running" &&
                  job.progress_percentage !== undefined && (
                    <div className="mb-3">
                      <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
                        <div
                          className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${job.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                  )}

                {/* Job Details */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">
                      Total:
                    </span>
                    <span className="ml-2 font-medium text-neutral-900 dark:text-neutral-100">
                      {job.total_tickets || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">
                      Processed:
                    </span>
                    <span className="ml-2 font-medium text-green-600 dark:text-green-400">
                      {job.processed_tickets}
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">
                      Failed:
                    </span>
                    <span className="ml-2 font-medium text-red-600 dark:text-red-400">
                      {job.failed_tickets}
                    </span>
                  </div>
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">
                      Skipped:
                    </span>
                    <span className="ml-2 font-medium text-yellow-600 dark:text-yellow-400">
                      {job.skipped_tickets}
                    </span>
                  </div>
                </div>

                {/* Cancel Button for Running Jobs */}
                {job.status === "running" && (
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => handleCancelSync(job.job_id)}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Cancel Job
                    </button>
                  </div>
                )}

                {/* Error Message */}
                {job.error_message && (
                  <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-300">
                      {job.error_message}
                    </p>
                  </div>
                )}
              </div>
            ))}

            {syncJobs.length === 0 && !loading && (
              <div className="p-12 text-center">
                <RefreshCw className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
                <p className="text-neutral-500 dark:text-neutral-400">
                  No sync jobs found. Start your first sync to see jobs here.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Tab */}
      {activeTab === "search" && <TicketSearchInterface />}

      {/* Sync Modal */}
      {showSyncModal && (
        <TicketSyncModal
          onClose={() => setShowSyncModal(false)}
          onStartSync={handleStartSync}
        />
      )}
    </div>
  );
};

export default TicketManagement;
